import { defaultTransformFn } from "api/rtkQueryApi/DsApi";
import { Agreement } from "../entities/Agreement";
import { Plan } from "../entities/Plan";
import moment from "moment";
import { businessesApi } from "./BusinessesApi";

const plansTags = businessesApi.enhanceEndpoints({
  addTagTypes: ["Plans", "Agreement"],
});

const plansApi = plansTags.injectEndpoints({
  endpoints: (build) => ({
    getAllPlans: build.query<Plan[], void>({
      query: () => ({
        url: `plans`,
        method: "GET",
      }),
      transformResponse: defaultTransformFn,
      providesTags: ["Plans"],
    }),
    getMyPlans: build.query<Plan[], void>({
      query: () => ({
        url: `plans/myPlans`,
        method: "GET",
      }),
      transformResponse: defaultTransformFn,
      providesTags: ["Plans"],
    }),
    getGrowthCoachPlans: build.query<Plan[], { email: string }>({
      query: ({ email }) => ({
        url: `plans/growthCoach/${email}`,
        method: "GET",
      }),
      transformResponse: defaultTransformFn,
      providesTags: ["Plans"],
    }),
    emailPdf: build.mutation<boolean, { businessId: string; file: File }>({
      query: ({ businessId, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `plans/emailPdf?businessId=${businessId}`,
          method: "POST",
          headers: {
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        };
      },
      transformResponse: defaultTransformFn,
    }),
    activatePlan: build.mutation<Plan, { planId: string }>({
      query: ({ planId }) => {
        return {
          url: `plans/${planId}/activate`,
          method: "POST",
        };
      },
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plans" },
        { type: "Plans", id: planId },
        { type: "BusinessPlans", id: result?.businessId },
      ],
    }),
    createAgreement: build.mutation<
      Agreement,
      { planId: string; content: string; pdfUrl: string }
    >({
      query: ({ planId, content, pdfUrl }) => {
        return {
          url: `plans/${planId}/createAgreement`,
          method: "POST",
          data: {
            content: content,
            pdfUrl: pdfUrl,
          },
        };
      },
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { planId }) => [
        { type: "Agreement", id: planId },
        { type: "Plans", id: planId },
        { type: "BusinessPlans", id: result?.businessId },
      ],
    }),
    updatePlanStartDate: build.mutation<
      Plan,
      { planId: string; startDate: string }
    >({
      query: ({ planId, startDate }) => {
        return {
          url: `plans/${planId}/updateStartDate`,
          method: "POST",
          data: {
            startDate: startDate,
          },
        };
      },
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plans" },
        { type: "Plans", id: planId },
        { type: "BusinessPlans", id: result?.businessId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllPlansQuery,
  useGetMyPlansQuery,
  useGetGrowthCoachPlansQuery,
  useEmailPdfMutation,
  useCreateAgreementMutation,
  useActivatePlanMutation,
  useUpdatePlanStartDateMutation,
} = plansApi;
